import React, { useEffect, useRef, useState } from "react";
import { Modal } from "antd";
import { useTranslation } from "react-i18next";
import { useMediaQuery } from "react-responsive";
import { getTermsDocument } from "lang/terms";
import style from "css/termsModal.module.css";

/**
 * 파일명: termsModal.jsx
 * 설명: 약관 전문 모달. type 으로 문서를 지정하고 현재 i18n 언어의 전문을 렌더링한다.
 *
 * props
 *  - type    : "privacy" | "service" | null (null 이면 닫힌 상태)
 *  - onClose : 닫기 핸들러
 */
function renderBlock(block, index) {
  if (block.type === "ul") {
    return (
      <ul
        key={index}
        className={style.list}
      >
        {block.items.map((item, itemIndex) => (
          <li key={itemIndex}>{item}</li>
        ))}
      </ul>
    );
  }

  if (block.type === "ol") {
    return (
      <ol
        key={index}
        className={style.orderedList}
      >
        {block.items.map((item, itemIndex) => (
          <li key={itemIndex}>{item}</li>
        ))}
      </ol>
    );
  }

  return (
    <p
      key={index}
      className={style.paragraph}
    >
      {block.text}
    </p>
  );
}

//동의를 받는 문서 (개인정보처리방침은 공개 문서이므로 제외)
const AGREEABLE_TYPES = ["collect", "service"];

function TermsModal({ type, onClose, onAgree }) {
  const { t, i18n } = useTranslation();
  const isMobile = useMediaQuery({ query: "(max-width: 767px)" });
  const bodyRef = useRef(null);

  //닫히는 애니메이션 동안 본문이 비지 않도록 마지막 문서 종류를 유지
  const [renderType, setRenderType] = useState(type);

  useEffect(() => {
    if (type !== null && type !== undefined) {
      setRenderType(type);
    }
  }, [type]);

  const open = type !== null && type !== undefined;
  const doc = getTermsDocument(renderType, i18n.language);

  //문서가 바뀌거나 다시 열릴 때 스크롤 위치 초기화
  useEffect(() => {
    if (open && bodyRef.current !== null) {
      bodyRef.current.scrollTop = 0;
    }
  }, [open, renderType, i18n.language]);

  const sections = doc?.sections ?? [];
  const hasContent = sections.length > 0;

  //동의 대상 문서일 때만 버튼이 "동의합니다"가 되고 클릭 시 해당 항목이 체크된다.
  const canAgree =
    onAgree !== undefined && AGREEABLE_TYPES.includes(renderType);

  const handleFooterClick = () => {
    if (canAgree) {
      onAgree(renderType);
    }
    onClose();
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      centered
      className={style.modal}
      width={isMobile ? "100%" : 720}
      title={doc?.title ?? ""}
      footer={
        <button
          type="button"
          className={style.closeBtn}
          onClick={handleFooterClick}
        >
          {canAgree ? t("terms.agree") : t("terms.close")}
        </button>
      }
    >
      <div
        className={style.scrollArea}
        ref={bodyRef}
      >
        {doc?.notice !== undefined && (
          <p className={style.notice}>{doc.notice}</p>
        )}
        {doc?.intro !== undefined && (
          <div className={style.intro}>
            {doc.intro.map((block, index) => renderBlock(block, index))}
          </div>
        )}
        {hasContent ? (
          sections.map((section, index) => (
            <section
              className={style.section}
              key={index}
            >
              <h3 className={style.sectionTitle}>{section.title}</h3>
              {section.blocks.map((block, blockIndex) =>
                renderBlock(block, blockIndex),
              )}
            </section>
          ))
        ) : (
          <p className={style.paragraph}>{t("terms.empty")}</p>
        )}
      </div>
    </Modal>
  );
}

export default TermsModal;
